/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useRef } from "react";
import {
  Breadcrumb, Layout, Button, message
} from "antd";
import '../CSS/Copilot.css'
import Nav from "../components/Nav";
import HeaderJs from "../components/HeaderJs";
import FooterJs from "../components/FooterJs";
// removed direct msalInstance import - use msal-react instead
import { useMsal } from "@azure/msal-react";

const { Header, Content } = Layout;

// NEW: accept props for embedded mode + contract context + containerId
const Copilot = ({
  embedded = false,
  context = null,
  containerId
}) => {
  const { instance, accounts } = useMsal(); // use app-wide MSAL instance
  const [user, setUser] = useState(null);
  const [msalReady, setMsalReady] = useState(true); // provider manages init
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const chatRenderedRef = useRef(false); // avoid duplicate renders

  const LOCAL_ACCOUNT_KEY = 'msalActiveAccount';

  const tokenEndpoint =
    'https://a2af0bffc97ee451915119167c6f52.52.environment.api.powerplatform.com/powervirtualagents/botsbyschema/aj_sciaTrial/directline/token?api-version=2022-03-01-preview';

  // pick container id per usage (avoid id clashes if mounted multiple times)
  const chatContainerId = containerId || (embedded ? 'webchat-embedded' : 'webchat');

  // Watch app-level accounts and set user immediately when app login occurs
  useEffect(() => {
    if (!instance) return;
    // prefer actual MSAL cached accounts (works when cacheLocation = localStorage)
    const cachedAccounts = instance.getAllAccounts ? instance.getAllAccounts() : [];
    if (cachedAccounts && cachedAccounts.length > 0) {
      const acct = instance.getActiveAccount() || cachedAccounts[0];
      instance.setActiveAccount(acct);
      setUser(acct);
      try { localStorage.setItem(LOCAL_ACCOUNT_KEY, JSON.stringify(acct)); } catch (e) { /* ignore */ }
      return;
    }

    // fallback: try to restore an account object we saved previously
    try {
      const saved = localStorage.getItem(LOCAL_ACCOUNT_KEY);
      if (saved) {
        const acct = JSON.parse(saved);
        // setActiveAccount with the object MSAL expects (if present)
        try { instance.setActiveAccount(acct); } catch (e) { /* ignore if not applicable */ }
        setUser(acct);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Failed to restore account from localStorage', err);
      setUser(null);
    }
    // no local initialization here — MSAL is provided by MsalProvider at app root
  }, [accounts, instance]);

  // render chat when user becomes available
  useEffect(() => {
    if (user && !chatRenderedRef.current) {
      renderChatWidget().catch(err => console.error('renderChatWidget failed', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogin = async () => {
    if (!instance) return;
    setIsLoggingIn(true);
    let hide;
    try {
      hide = message.loading('Opening Microsoft sign-in…', 0);
      const redirectUri = typeof window !== 'undefined' ? window.location.origin : undefined;

      // Use the app-wide instance to login (popup preferred)
      const loginResult = await instance.loginPopup({
        scopes: ['User.Read', 'openid', 'profile'],
        prompt: 'select_account',
        redirectUri
      });

      // store active account persistently so other pages/components can pick it up
      const accountsLocal = instance.getAllAccounts();
      if (accountsLocal && accountsLocal.length > 0) {
        const acct = instance.getActiveAccount() || accountsLocal[0];
        instance.setActiveAccount(acct);
        setUser(acct);
        try { localStorage.setItem(LOCAL_ACCOUNT_KEY, JSON.stringify(acct)); } catch (e) { /* ignore */ }
      }

    
    } catch (err) {
      console.error('loginPopup failed:', err);
      // fallback to redirect for popup blocked
      try {
        await instance.loginRedirect({ scopes: ['User.Read', 'openid', 'profile'] });
      } catch (e) {
        message.error('Login failed. Please try again.');
      }
    } finally {
      if (hide) hide();
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    if (!instance) return;
    try {
      await instance.logoutPopup({ account: instance.getActiveAccount() });
      setUser(null);
      try { localStorage.removeItem(LOCAL_ACCOUNT_KEY); } catch (e) { /* ignore */ }
      // avoid full reload in embedded mode; in full app you may reload if needed
      if (!embedded) window.location.reload();
    } catch (err) {
      console.error(err);
    }
  };

  const getOAuthCardResourceUri = (activity) => {
    if (
      activity &&
      activity.attachments &&
      activity.attachments[0] &&
      activity.attachments[0].contentType === 'application/vnd.microsoft.card.oauth' &&
      activity.attachments[0].content.tokenExchangeResource
    ) {
      return activity.attachments[0].content.tokenExchangeResource.uri;
    }
    return null;
  };

  // Use the instance from useMsal for token exchange
  const exchangeTokenAsync = async (resourceUri) => {
    if (!instance) return null;
    const users = instance.getAllAccounts();
    if (users.length <= 0) return null;
    const account = instance.getActiveAccount() || users[0];
    const tokenRequest = {
      scopes: [resourceUri],
      account
    };
    try {
      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
      return tokenResponse.accessToken;
    } catch (err) {
      console.error('acquireTokenSilent error', err);
      // You may optionally try an interactive flow here if silent fails
      return null;
    }
  };

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        accept: 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch JSON due to ${res.status}`);
    }
    return await res.json();
  };

  const renderChatWidget = async () => {
    // guard to avoid double render
    if (chatRenderedRef.current) return;
    // Ensure WebChat script is available
    if (!window.WebChat) {
      const webChatScript = document.createElement('script');
      webChatScript.src = 'https://cdn.botframework.com/botframework-webchat/latest/webchat.js';
      webChatScript.async = true;
      document.body.appendChild(webChatScript);
      await new Promise(resolve => {
        webChatScript.onload = resolve;
        // fallback resolve after short timeout
        setTimeout(resolve, 2000);
      });
      if (!window.WebChat) {
        console.error('WebChat failed to load.');
        return;
      }
    }

    chatRenderedRef.current = true;

    const userID = user?.localAccountId
      ? user.localAccountId.substr(0, 36)
      : (Math.random().toString() + Date.now().toString()).substr(0, 64);

    const { token } = await fetchJSON(tokenEndpoint);
    const directLine = window.WebChat.createDirectLine({
      token: token,
      domain: 'https://europe.directline.botframework.com/v3/directline'
    });

    const store = window.WebChat.createStore(
      {},
      ({ dispatch }) => next => action => {
        const { type } = action;

        if (type === 'DIRECT_LINE/CONNECT_FULFILLED') {
          dispatch({
            meta: { method: 'keyboard' },
            payload: {
              activity: {
                channelData: { postBack: true },
                name: 'startConversation',
                type: 'event',
                from: { id: userID, name: user?.name, role: 'user' },
                value: {
                  locale: navigator?.language || 'en-US',
                  timezone:
                    (Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone) || 'UTC',
                  ...(context ? { context } : {}),
                  ctx_var_SourceData: context?.selectedContracts || [],
                  ctx_var_SourceScreen: context?.sourcePage || 'SciaBot'
                }
              },
            },
            type: 'DIRECT_LINE/POST_ACTIVITY',
          });
        }

        if (type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
          const activity = action.payload.activity;
          let resourceUri;
          if (
            activity.from.role === 'bot' &&
            (resourceUri = getOAuthCardResourceUri(activity))
          ) {
            exchangeTokenAsync(resourceUri).then(token => {
              if (token) {
                directLine
                  .postActivity({
                    type: 'invoke',
                    name: 'signin/tokenExchange',
                    value: {
                      id: activity.attachments[0].content.tokenExchangeResource.id,
                      connectionName: activity.attachments[0].content.connectionName,
                      token,
                    },
                    from: {
                      id: userID,
                      name: user?.name,
                      role: 'user'
                    },
                  })
                  .subscribe(
                    id => {
                      if (id === 'retry') return next(action);
                    },
                    error => next(action)
                  );
                return;
              } else {
                return next(action);
              }
            });
          } else {
            return next(action);
          }
        } else {
          return next(action);
        }
      }
    );

    const styleOptions = {
      hideUploadButton: true,
      botAvatarImage:
        'https://bot-framework.azureedge.net/bot-icons-v1/6ab9b101-b65c-4357-9e9f-915cbf313a14_2K5Bt02aW8egEb97fxAgh7vqChK4UV3Nh3Lw3YYArhEKR8mB.png',
      botAvatarInitials: 'Bot',
      userAvatarImage:
        'https://content.powerapps.com/resource/makerx/static/media/user.0d06c38a.svg',
      userAvatarInitials: 'User',
    };

    window.WebChat.renderWebChat(
      {
        directLine,
        store,
        userID,
        styleOptions,
      },
      document.getElementById(chatContainerId)
    );
  };

  // --- UI ---
  if (embedded) {
    // Minimal UI for modal embedding
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div style={{ padding: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#666' }}>
            {user ? `Signed in as ${user.name}` : 'Not signed in'}
          </span>
          {/* {!user && (
            <Button id="login" type="primary" onClick={() => { handleLogin() }} size="small"
              disabled={!msalReady || isLoggingIn}
              loading={isLoggingIn}
            >
              Login
            </Button>
          )}
          {user && (
            <Button id="logout" onClick={() => { handleLogout() }} danger size="small">
              Logout
            </Button>
          )} */}
        </div>
        <div id={chatContainerId} role="main" style={{ flex: 1 }} />
      </div>
    );
  }


  // Original full-page layout (unchanged)
  return (
    <div className="SCAUX">
      <Layout>
        <Header className="header">
          <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no" />
          <div id={chatContainerId} role="main"></div>
          <HeaderJs />
        </Header>
        <Layout>
          <Nav />
          <Content>
            <div className="content">
              <div id="scrollableDiv" className="scrollable-content">
                <div className="searchfilter">
                  <Breadcrumb className="page-title">
                    <Breadcrumb.Item>
                      <span className="page-title-label">Supplier Contracts Copilot</span>
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </div>
                <div id="chatwindow">
                  <div id="subheader">
                    <p id="loginStatus">
                      {user ? `Currently logged in as ${user.name} on the website.` : 'Not logged in'}
                    </p>
                    {/* <Button id="login" type="primary" onClick={() => { handleLogin() }} style={{ marginLeft: 10 }}>
                      Login
                    </Button>
                    <Button
                      id="logout"
                      onClick={() => { handleLogout() }}
                      style={{ marginLeft: 10, display: user ? 'inline' : 'none' }}
                      danger
                    >
                      Logout
                    </Button> */}
                  </div>
                </div>
              </div>
            </div>
          </Content>
        </Layout>
        <FooterJs />
      </Layout>
    </div>
  );
};
export default Copilot;